import { getItemPrice } from "@shared/modules/financials/utils/price-utils";
import {
  CartPermissionType,
  MapPermissionsType,
  MultiSeatSelectActions,
  SeatDataType,
  SeatsToMultiSelectType,
} from "@shared/modules/seating/types/seating-types";
import { useUserVenueSession } from "@shared/modules/venue/hooks/useUserVenueSession";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { getExceptionToastDescription } from "@shared/utils/errors/error-utils";
import { useState } from "react";
import { useMultiSeatSelectType } from "./useMultiSeatSelect.props";

const notifier = NotifierFactory.createNotifier();

/**
 * Contains functions for selecting multiple seats on a seating map.
 * Logic is separated from useSeatingState so it can be easily updated or removed in the future.
 */
export function useMultiSeatSelect(props: useMultiSeatSelectType) {
  const {
    cartPermissions,
    getSeatInCartPermissions,
    clearSelections,
    setIsUpdatingMap,
    updateSeatingState,
    awaitModal,
    requestAddSeatPermissions,
    requestRemoveSeatPermissions,
    stageServiceState,
    disableBestAvailableSeating,
    synchronizeSeating,
    seatingItemArray,
  } = props;

  const itemTypesSet = seatingItemArray?.map((item) => ({
    id: item.id,
    price: item.price,
  }));
  const [seatsToMultiSelect, setSeatsToMultiSelect] =
    useState<SeatsToMultiSelectType>();

  const { data: currentVenue } = useUserVenueSession();

  /**
   * Gets the IDs of the seats to add or remove when selecting multiple seats at once
   */
  function formatSeatsForMultiSelect(seats: Array<SeatDataType>) {
    const itemIdsToAdd: Array<number> = [];
    const itemIdsToRemove: Array<number> = [];
    let action: MultiSeatSelectActions;

    // if every selected seat is already in the cart, show the modal in removal mode
    if (seats.every((seat) => getSeatInCartPermissions(seat.$data.id))) {
      action = MultiSeatSelectActions.Remove;
      // iterate over the seats and add the item ids to itemIdsToRemove
      seats.forEach((seat) => {
        const itemTypeId = cartPermissions.find(
          (item: CartPermissionType) =>
            item.seat && item.seat.id === seat.$data.id
        )?.itemTypeId;

        // if the id doesn't already exist in itemIdsToRemove, add it
        if (
          itemTypeId &&
          !itemIdsToRemove.find((itemId) => itemId === itemTypeId)
        ) {
          itemIdsToRemove.push(itemTypeId);
        }
      });
    }
    // else some selected seats are not in the cart, show the modal in add mode
    else {
      action = MultiSeatSelectActions.Add;
      // iterate over the seats and add the item ids to itemIdsToAdd
      seats.forEach((seat) => {
        seat.$permissions?.forEach((seatPermission: MapPermissionsType) => {
          const itemTypeId = seatPermission.seatingItemTypeId;

          // if the id doesn't already exist in itemIdsToAdd, add it
          if (!itemIdsToAdd.find((itemId) => itemId === itemTypeId)) {
            itemIdsToAdd.push(itemTypeId);
          }
        });
      });
    }

    return { itemIdsToAdd, itemIdsToRemove, action };
  }

  /**
   * Handles the selection of multiple seats.
   * Reduces the list of seats to the available items on all of those seats (no duplicates).
   */
  async function onMultiSeatSelect(
    seats: Array<SeatDataType>
  ): Promise<Array<SeatDataType> | null> {
    const { itemIdsToAdd, itemIdsToRemove, action } =
      formatSeatsForMultiSelect(seats);

    try {
      setSeatsToMultiSelect({
        seats,
        itemIdsToAdd,
        itemIdsToRemove,
        action,
      });

      const selectedSeats = await awaitModal();

      // add or remove seats from clicking and dragging
      setIsUpdatingMap(true);
      if (!disableBestAvailableSeating && stageServiceState) {
        stageServiceState.destroyExistingSeatsHighlighted();
        selectedSeats.forEach((seat) => {
          const itemType = itemTypesSet?.find(
            (item) => item.id === seat.itemTypeId
          );

          /**
           * Format seat data to be compatible with both best available and individual seat selection.
           * Using key/value pair structure in the maps ensures consistent handling in seating visualization,
           * regardless of selection method (best available or individual selection).
           */
          const seatData = {
            seatIdentifiers: [seat.$data.identifier],
            segmentIdentifier: seat.$data.segment_identifier,
            locationIdentifier: seat.$data.location_identifier,
            permissionsMap: {
              [seat.$data.id]: seat.permissionId,
            },
            ticketTypesMap: seat.itemTypeId
              ? {
                  [seat.$data.id]: seat.itemTypeId,
                }
              : {},
            pricingMap: {
              [seat.$data.id]: `${getItemPrice(
                itemType?.price,
                Boolean(currentVenue?.all_in_pricing_is_enabled)
              )}`,
            },
            seatIds: [seat.$data.id],
          };

          stageServiceState.handleSeatingVisualization({
            seats: seatData,
            selectedIndividually: true,
            cb: () => {
              if (synchronizeSeating) {
                synchronizeSeating();
              }
            },
          });
        });
        if (synchronizeSeating) {
          synchronizeSeating();
        }
        setIsUpdatingMap(false);
      } else if (action === MultiSeatSelectActions.Add) {
        await requestAddSeatPermissions(selectedSeats);
      } else if (action === MultiSeatSelectActions.Remove) {
        await requestRemoveSeatPermissions(selectedSeats);
      }
      // handle map updates and errors
      updateSeatingState();
    } catch (error) {
      // show an error toast
      // (null value is returned when the modal is cancelled, don't show error for that)
      if (error instanceof Error) {
        notifier.error(getExceptionToastDescription(error));
      }
    } finally {
      // reset multiSeatSelect and deselect seats
      setSeatsToMultiSelect(undefined);
      if (disableBestAvailableSeating) {
        clearSelections();
      }
    }
    return seats;
  }

  return {
    seatsToMultiSelect,
    onMultiSeatSelect,
  };
}
